// =============================================================================
// ModifierForm — create / edit a CombatModifier
// =============================================================================
import { useState } from 'react';
import { Plus, Trash2, X, Check } from 'lucide-react';
import type { CombatModifierWithStates, ModifierEffect } from '../../../../backend/shared/types/index';
import type { RuleSystem } from '../../../../backend/shared/types/index';
import { groupedAttributes } from '@/config/combatAttributes';

interface BonusTypeOption { typeId: string; name: string; stackingRule: string }

interface Props {
  initial?:              Partial<CombatModifierWithStates>;
  bonusTypes:            BonusTypeOption[];
  gameSystem?:           RuleSystem;
  saving:                boolean;
  /** When true, show the ownerType selector (DM/admin contexts). */
  canSetOwnerType?:      boolean;
  /** Pre-fill ownerType for the new modifier (default: 'world'). */
  defaultOwnerType?:     'character' | 'world' | 'self-only';
  /** Pre-fill the ownerCharacterId for character-type modifiers. */
  defaultOwnerCharId?:   string;
  defaultOwnerCharName?: string;
  onSave:                (data: ModifierFormData) => void;
  onCancel:              () => void;
}

export interface ModifierFormData {
  name:               string;
  sourceType:         string;
  sourceName:         string;
  sourceId?:          string;
  effects:            ModifierEffect[];
  worksInAntimagic:   boolean;
  defaultEnabled:     boolean;
  isActive:           boolean;
  ownerType:          'character' | 'world' | 'self-only';
  ownerCharacterId?:  string;
  ownerCharacterName?: string;
}

const SOURCE_TYPES = ['character', 'npc', 'item', 'spell', 'ability'] as const;

// Target attributes valid for advantage/disadvantage (d20-roll targets only)
const ROLL_MODIFIER_TARGETS = [
  { value: 'attack',        label: 'All Attack Rolls'       },
  { value: 'attack.melee',  label: 'Melee Attack Rolls'     },
  { value: 'attack.ranged', label: 'Ranged Attack Rolls'    },
  { value: 'attack.spell',  label: 'Spell Attack Rolls'     },
  { value: 'cmb',           label: 'Combat Maneuvers (CMB)' },
  { value: 'save.all',      label: 'All Saving Throws'      },
  { value: 'save.fort',     label: 'Fortitude Save'         },
  { value: 'save.ref',      label: 'Reflex Save'            },
  { value: 'save.will',     label: 'Will Save'              },
  { value: 'save.str',      label: 'Strength Save'          },
  { value: 'save.dex',      label: 'Dexterity Save'         },
  { value: 'save.con',      label: 'Constitution Save'      },
  { value: 'save.int',      label: 'Intelligence Save'      },
  { value: 'save.wis',      label: 'Wisdom Save'            },
  { value: 'save.cha',      label: 'Charisma Save'          },
];

function blankEffect(): ModifierEffect {
  return {
    effectId:        crypto.randomUUID(),
    bonusTypeId:     'untyped',
    value:           0,
    targetAttribute: 'ac',
    targetLabel:     'Armor Class',
    condition:       undefined,
  };
}

function blankRollModifierEffect(kind: 'advantage' | 'disadvantage'): ModifierEffect {
  return {
    effectId:        crypto.randomUUID(),
    rollModifier:    kind,
    targetAttribute: 'attack',
    targetLabel:     'All Attack Rolls',
    condition:       undefined,
  };
}

export default function ModifierForm({ initial, bonusTypes, gameSystem, saving, canSetOwnerType, defaultOwnerType, defaultOwnerCharId, defaultOwnerCharName, onSave, onCancel }: Props) {
  const sys = gameSystem ?? 'pathfinder1';
  const attrGroups = groupedAttributes(sys);

  const [name,           setName]           = useState(initial?.name ?? '');
  const [sourceType,     setSourceType]     = useState<typeof SOURCE_TYPES[number]>(
    (initial?.sourceType as typeof SOURCE_TYPES[number]) ?? 'character',
  );
  const [sourceName,     setSourceName]     = useState(initial?.sourceName ?? '');
  const [worksInAntimagic, setWorksInAntimagic] = useState(initial?.worksInAntimagic ?? false);
  const [defaultEnabled, setDefaultEnabled] = useState(initial?.defaultEnabled ?? true);
  const [isActive,       setIsActive]       = useState(initial?.isActive ?? true);
  const [ownerType,      setOwnerType]      = useState<'character'|'world'|'self-only'>(
    (initial as any)?.ownerType ?? defaultOwnerType ?? 'world'
  );
  const [effects,        setEffects]        = useState<ModifierEffect[]>(
    initial?.effects?.length ? initial.effects : [blankEffect()],
  );
  const [error, setError] = useState<string | null>(null);

  function updateEffect(idx: number, patch: Partial<ModifierEffect>) {
    setEffects((prev) => prev.map((e, i) => i === idx ? { ...e, ...patch } : e));
  }

  function addEffect() {
    setEffects((prev) => [...prev, blankEffect()]);
  }

  function removeEffect(idx: number) {
    setEffects((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleSave() {
    setError(null);
    if (!name.trim()) { setError('Name is required'); return; }
    if (!sourceName.trim()) { setError('Source name is required'); return; }
    if (effects.length === 0) { setError('At least one effect is required'); return; }
    for (const e of effects) {
      if (!e.rollModifier && (e.value === 0 || e.value === undefined)) {
        setError('Effect value cannot be 0'); return;
      }
      if (!e.targetLabel.trim()) { setError('Effect target label is required'); return; }
    }
    onSave({
      name: name.trim(), sourceType, sourceName: sourceName.trim(), effects, worksInAntimagic, defaultEnabled, isActive,
      ownerType,
      ownerCharacterId:   (ownerType === 'character' || ownerType === 'self-only') ? (defaultOwnerCharId ?? (initial as any)?.ownerCharacterId) : undefined,
      ownerCharacterName: (ownerType === 'character' || ownerType === 'self-only') ? (defaultOwnerCharName ?? (initial as any)?.ownerCharacterName) : undefined,
    });
  }

  const selectCls = 'w-full bg-darkbg border border-darkbg-border rounded px-2 py-1.5 text-parchment text-xs focus:outline-none focus:border-gold';
  const inputCls  = 'w-full bg-darkbg border border-darkbg-border rounded px-2 py-1.5 text-parchment text-xs placeholder-parchment-muted/30 focus:outline-none focus:border-gold';

  return (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-xs text-parchment-muted/60 mb-1">Modifier Name *</label>
        <input value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Inspire Greatness"
          className={inputCls} maxLength={100} />
      </div>

      {/* Source */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-parchment-muted/60 mb-1">Source Type *</label>
          <select value={sourceType} onChange={(e) => setSourceType(e.target.value as typeof SOURCE_TYPES[number])} className={selectCls}>
            {SOURCE_TYPES.map((t) => (
              <option key={t} value={t} className="capitalize">{t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-parchment-muted/60 mb-1">Source Name *</label>
          <input value={sourceName} onChange={(e) => setSourceName(e.target.value)}
            placeholder="e.g. Piper (Bard 16)"
            className={inputCls} maxLength={100} />
        </div>
      </div>

      {/* Effects */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs text-parchment-muted/60">Effects *</label>
          <div className="flex items-center gap-2">
            <button onClick={() => setEffects((p) => [...p, blankRollModifierEffect('advantage')])}
              className="flex items-center gap-1 text-xs text-gold/70 hover:text-gold transition-colors">
              <Plus className="w-3 h-3" /> Advantage
            </button>
            <button onClick={() => setEffects((p) => [...p, blankRollModifierEffect('disadvantage')])}
              className="flex items-center gap-1 text-xs text-gold/70 hover:text-gold transition-colors">
              <Plus className="w-3 h-3" /> Disadvantage
            </button>
            <button onClick={addEffect}
              className="flex items-center gap-1 text-xs text-gold/70 hover:text-gold transition-colors">
              <Plus className="w-3 h-3" /> Numeric bonus
            </button>
          </div>
        </div>
        <div className="space-y-2">
          {effects.map((effect, idx) => (
            <div key={effect.effectId} className="bg-darkbg-tertiary rounded p-2 space-y-2">

              {effect.rollModifier ? (
                /* ── Roll modifier effect (Advantage / Disadvantage) ── */
                <div className="grid grid-cols-[auto_1fr] gap-2 items-end">
                  <div>
                    <label className="block text-[10px] text-parchment-muted/40 mb-0.5">Effect</label>
                    <select
                      value={effect.rollModifier}
                      onChange={(e) => updateEffect(idx, { rollModifier: e.target.value as 'advantage' | 'disadvantage' })}
                      className={selectCls}
                    >
                      <option value="advantage">Advantage</option>
                      <option value="disadvantage">Disadvantage</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] text-parchment-muted/40 mb-0.5">Applies to</label>
                    <select
                      value={effect.targetAttribute}
                      onChange={(e) => {
                        const opt = ROLL_MODIFIER_TARGETS.find((a) => a.value === e.target.value);
                        updateEffect(idx, {
                          targetAttribute: e.target.value,
                          targetLabel:     opt?.label ?? e.target.value,
                        });
                      }}
                      className={selectCls}
                    >
                      {ROLL_MODIFIER_TARGETS.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                /* ── Numeric bonus effect ── */
                <div className="grid grid-cols-[80px_1fr_1fr] gap-2">
                  <div>
                    <label className="block text-[10px] text-parchment-muted/40 mb-0.5">Value</label>
                    <input
                      type="number" value={effect.value ?? 0}
                      onChange={(e) => updateEffect(idx, { value: parseInt(e.target.value) || 0 })}
                      className={inputCls + ' text-center'} min={-99} max={99}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-parchment-muted/40 mb-0.5">Bonus Type</label>
                    <select value={effect.bonusTypeId ?? 'untyped'}
                      onChange={(e) => updateEffect(idx, { bonusTypeId: e.target.value })}
                      className={selectCls}>
                      {bonusTypes.map((t) => (
                        <option key={t.typeId} value={t.typeId}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] text-parchment-muted/40 mb-0.5">Applies to</label>
                    <select
                      value={effect.targetAttribute}
                      onChange={(e) => {
                        const opt = Object.values(attrGroups).flat().find((a) => a.value === e.target.value);
                        updateEffect(idx, {
                          targetAttribute: e.target.value,
                          targetLabel:     opt?.label ?? e.target.value,
                        });
                      }}
                      className={selectCls}
                    >
                      {Object.entries(attrGroups).map(([group, attrs]) => (
                        <optgroup key={group} label={group}>
                          {attrs.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Condition + remove */}
              <div className="flex items-center gap-2">
                <input value={effect.condition ?? ''}
                  onChange={(e) => updateEffect(idx, { condition: e.target.value || undefined })}
                  placeholder='Condition (optional) — e.g. "when flanking"'
                  className={inputCls + ' flex-1'} maxLength={200} />
                {effects.length > 1 && (
                  <button onClick={() => removeEffect(idx)}
                    className="p-1 text-danger/60 hover:text-danger transition-colors shrink-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Owner type — only shown when the DM/admin is creating in a campaign context */}
      {canSetOwnerType && (
        <>
          <div className="space-y-2.5 border-l-2 border-gold/30 pl-3">
            <span className="text-xs text-parchment-muted/60 font-medium uppercase tracking-wide">Modifier Scope</span>
            <div className="flex flex-col gap-2">
              {(['world', 'character', 'self-only'] as const).map((t) => (
                <label key={t} className="flex items-start gap-2.5 cursor-pointer">
                  <input type="radio" checked={ownerType === t} onChange={() => setOwnerType(t)}
                    className="accent-gold mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-parchment/80 font-medium block">
                      {t === 'world'
                        ? 'Party-Wide'
                        : t === 'character'
                        ? 'Spell / Ability (Target Others)'
                        : 'Personal Ability (Self-Only)'}
                    </span>
                    <span className="text-[11px] text-parchment-muted/60 block leading-snug">
                      {t === 'world'
                        ? 'DM-managed buff/debuff. Applied to all party members by default.'
                        : t === 'character'
                        ? 'Spell or ability targeting one or more party members (e.g., Haste, Bless). Activate the modifier, then toggle individual characters in the Active for Characters list.'
                        : 'Personal ability like Power Attack or Rage. Only you see this modifier; other characters cannot toggle it.'}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Instructions for character-specific targeting */}
          {ownerType === 'character' && (
            <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-2.5 text-[11px] leading-relaxed text-indigo-300/70 space-y-1">
              <p className="font-medium text-indigo-300">How to specify targets:</p>
              <ol className="list-decimal list-inside space-y-0.5 ml-0">
                <li>Save this modifier with the checkboxes below</li>
                <li>Check the <span className="text-gold font-medium">Active</span> toggle to enable it</li>
                <li>Expand the modifier and toggle individual character names in the <span className="text-gold font-medium">Active for Characters</span> list to specify who is affected</li>
              </ol>
            </div>
          )}
        </>
      )}

      {/* Options */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={worksInAntimagic}
            onChange={(e) => setWorksInAntimagic(e.target.checked)}
            className="rounded border-darkbg-border" />
          <span className="text-xs text-parchment-muted/70">Works in Antimagic?</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={defaultEnabled}
            onChange={(e) => setDefaultEnabled(e.target.checked)}
            className="rounded border-darkbg-border" />
          <span className="text-xs text-parchment-muted/70">Enabled for all characters by default</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="rounded border-darkbg-border" />
          <span className="text-xs text-parchment-muted/70">Active</span>
        </label>
      </div>

      {/* Error */}
      {error && <p className="text-danger text-xs">{error}</p>}

      {/* Actions */}
      <div className="flex items-center gap-2 justify-end pt-1">
        <button onClick={onCancel} disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-parchment-muted/60 hover:text-parchment border border-darkbg-border rounded transition-colors disabled:opacity-50">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs btn-primary disabled:opacity-50">
          {saving ? '…' : <><Check className="w-3.5 h-3.5" /> Save</>}
        </button>
      </div>
    </div>
  );
}
